import { ID } from 'types';
import { NsbOverrideColumn } from './nsbOverrideStore';

// Event-level NSB *method* selections, set by the StickyHeader method modals and read by the
// benchmark / baseline cell renderers. Module-level (singleton) so the toolbar and the cell
// renderers share one source of truth without threading it through React context.
//
// The toolbar picks a high-level METHOD per column; when the method is Smart Baseline Method it
// also picks a specific flavor (the event default flavor). The renderers consume a derived
// "selection" (a baseline-flavor id, 'mirror', or 'manual'). Other flavors are also reachable
// per-row (4b/4c) when a lot's "allow overrides" toggle is on.

export const MIRROR_STARTING_PRICE = 'mirror';
// Manual Entry: the buyer types the price per line item — no smart-baseline price is seeded and
// every Apply (4a/4b/4c) is disabled across all lots.
export const MANUAL_ENTRY = 'manual';
// Intelligent Counter Offer (Negotiation column only): like Manual Entry, no smart-baseline price is
// seeded — the row is left at NSP=0. Phase-1 reuse of the existing `set_baseline_average_on_first_round_quotes`
// toggle: the backend fills NSP from the lowest round-1 supplier bid.
export const INTELLIGENT_COUNTER_OFFER = 'intelligent_counter_offer';

// Negotiation Starting Price methods (mockup: Manual Entry / Intelligent Counter Offer / Smart Baseline).
export type NsbStartingMethod = 'intelligent_counter_offer' | 'manual_entry' | 'smart_baseline';
// Baseline Price per Unit methods (mockup: Mirror Negotiations / Manual Entry / ICO / Smart Baseline).
export type NsbBaselineMethod = NsbStartingMethod | 'mirror_negotiations';

// Smart Baseline Method defaults to Historical Average (F3) until the buyer picks another flavor.
const DEFAULT_SMART_BASELINE_FLAVOR = 'F3';

// Defaults (per UX): Negotiation = Smart Baseline Method; Baseline = Mirror Negotiations.
const DEFAULT_STARTING_METHOD: NsbStartingMethod = 'smart_baseline';
const DEFAULT_BASELINE_METHOD: NsbBaselineMethod = 'mirror_negotiations';

let startingMethod: NsbStartingMethod = DEFAULT_STARTING_METHOD;
let baselineMethod: NsbBaselineMethod = DEFAULT_BASELINE_METHOD;
// The chosen flavor for each column when its method is Smart Baseline Method.
let startingFlavor: string = DEFAULT_SMART_BASELINE_FLAVOR;
let baselineFlavor: string = DEFAULT_SMART_BASELINE_FLAVOR;

const subscribers = new Set<() => void>();

// Coalesced into a microtask, mirroring nsbRowConfigStore. Every NSB benchmark and baseline cell
// subscribes, so on a large event one event-level method or flavor change would otherwise fire a
// synchronous callback per cell per write. Any burst within a tick becomes one subscriber round.
let notifyScheduled = false;

// The one place subscribers are fired, and it owns the flag. Both paths go through it so a scheduled round
// cannot outlive a synchronous one: without the reset, a setter earlier in the same tick would leave a
// queued microtask that fires AFTER hydration and re-triggers the clobber the sync path exists to prevent.
// Nothing calls a setter before hydrating today, but that invariant was unstated and unenforced.
const flushNow = (): void => {
  notifyScheduled = false;
  subscribers.forEach(fn => fn());
};

const notify = (): void => {
  if (notifyScheduled) return;
  notifyScheduled = true;
  queueMicrotask(() => { if (notifyScheduled) flushNow(); });
};

// Hydration fires IMMEDIATELY rather than on the coalesced path. The event-load block in
// LineItemsProvider.fetchEvent restores the selection and THEN hydrates each row's config in one
// synchronous run, and subscribers act on the difference: BaselinePriceRenderer's selection handler
// invalidates a stored row so it re-derives. Deferred, that round lands after row hydration and
// discards the value hydration just restored, defeating the recompute gate. Firing here keeps the
// pre-coalescing order, and it is one round per event load - nothing to batch.

export const getStartingPriceMethod = (): NsbStartingMethod => startingMethod;
export const getBaselineMethod = (): NsbBaselineMethod => baselineMethod;
export const getStartingPriceFlavor = (): string => startingFlavor;
export const getBaselineFlavor = (): string => baselineFlavor;

export const setStartingPriceMethod = (value: NsbStartingMethod): void => {
  startingMethod = value;
  notify();
};

export const setBaselineMethod = (value: NsbBaselineMethod): void => {
  baselineMethod = value;
  notify();
};

export const setStartingPriceFlavor = (value: string): void => {
  startingFlavor = value;
  notify();
};

export const setBaselineFlavor = (value: string): void => {
  baselineFlavor = value;
  notify();
};

// Shape of the event-level selection persisted in ui_config.nsb (method + flavor per column).
export interface NsbPersistedSelection {
  baseline_flavor?: string;
  baseline_method?: NsbBaselineMethod;
  // Line items the buyer resolved by hand at the last save — see NsbEventConfig for why this exists.
  buyer_picked_line_item_ids?: ID[];
  starting_flavor?: string;
  starting_method?: NsbStartingMethod;
}

// Restored BEFORE any row hydrates, since hydration reads it to decide which rows were the buyer's.
// Empty for an event saved before this existed, where every sourced row is treated as the buyer's.
let buyerPickedIds = new Set<string>();
export const getBuyerPickedIds = (): Set<string> => buyerPickedIds;

// Whether the event actually CARRIED a picked list. An empty list ("the buyer picked nothing") and an
// absent one ("this event predates the list") both leave buyerPickedIds empty, but they mean opposite
// things: only the absent case may fall back to treating every sourced row as the buyer's.
let hasBuyerPicked = false;
export const hasBuyerPickedList = (): boolean => hasBuyerPicked;

// True when the picked list matches what hydration restored, so a save has nothing to persist. An event
// that never CARRIED a list is deliberately never "unchanged" — it has to write one at least once, or it
// falls back to treating every sourced row as the buyer's on each reload, forever.
export const isBuyerPickedListUnchanged = (ids: string[]): boolean => {
  if (!hasBuyerPicked) return false;
  if (ids.length !== buyerPickedIds.size) return false;

  return ids.every(id => buyerPickedIds.has(id));
};

// The current event-level method/flavor selections, for persisting to ui_config.nsb.
export const getNsbSelectionConfig = (): NsbPersistedSelection => ({
  baseline_flavor: baselineFlavor,
  baseline_method: baselineMethod,
  starting_flavor: startingFlavor,
  starting_method: startingMethod,
});

// Restore the event-level selections from a persisted config (ui_config.nsb) on event load. Sets all
// provided values and notifies once; missing keys keep the current default.
export const hydrateNsbSelection = (config?: NsbPersistedSelection | null): void => {
  if (!config) return;
  if (config.starting_method) startingMethod = config.starting_method;
  if (config.baseline_method) baselineMethod = config.baseline_method;
  if (config.starting_flavor) startingFlavor = config.starting_flavor;
  if (config.baseline_flavor) baselineFlavor = config.baseline_flavor;
  if (config.buyer_picked_line_item_ids) {
    buyerPickedIds = new Set(config.buyer_picked_line_item_ids.map(String));
    hasBuyerPicked = true;
  }
  flushNow();
};

// Derived effective selection consumed by the cell renderers: flavor id | 'mirror' | 'manual' | 'intelligent_counter_offer'.
export const getStartingPriceSelection = (): string => {
  switch (startingMethod) {
    case 'intelligent_counter_offer':
      return INTELLIGENT_COUNTER_OFFER;
    case 'manual_entry':
      return MANUAL_ENTRY;
    default:
      return startingFlavor;
  }
};

export const getBaselineSelection = (): string => {
  switch (baselineMethod) {
    case 'manual_entry':
      return MANUAL_ENTRY;
    case 'mirror_negotiations':
      return MIRROR_STARTING_PRICE;
    default:
      return baselineFlavor;
  }
};

// True only when a column's method is Smart Baseline Method — the precondition for any per-row
// Apply (the per-lot "allow overrides" toggle then governs whether it is actually enabled).
export const isSmartBaselineApplyAllowed = (column: NsbOverrideColumn): boolean =>
  (column === 'baseline' ? baselineMethod : startingMethod) === 'smart_baseline';

// Lets a consumer (selector display, or a cell renderer) re-read after a selection changes.
export const subscribeNsbSelection = (fn: () => void): (() => void) => {
  subscribers.add(fn);
  return () => { subscribers.delete(fn); };
};

// Restore the event-level selections to their defaults. Called on event change so a new event
// doesn't inherit the previous event's method/flavor choices (this store is a pure singleton with
// no event key of its own).
export const resetNsbSelection = (): void => {
  startingMethod = DEFAULT_STARTING_METHOD;
  baselineMethod = DEFAULT_BASELINE_METHOD;
  startingFlavor = DEFAULT_SMART_BASELINE_FLAVOR;
  baselineFlavor = DEFAULT_SMART_BASELINE_FLAVOR;
  buyerPickedIds = new Set();
  hasBuyerPicked = false;
  notify();
};
